// React / Next
import { Link } from '@tanstack/react-router';

// Components
import { SignatureA, Typography } from '@archieai/ui';

// Types
import type { ArticleType } from '../../lib/sanity';

type FooterLink = {
  label: string;
  href: string;
  external?: boolean;
  /** For resource type filter links */
  resourceType?: ArticleType;
  /** For action buttons that trigger a function instead of navigation */
  action?: () => void;
};

type FooterSection = {
  title: string;
  links: FooterLink[];
};

/** Opens the Delve cookie consent settings dialog */
const handleOpenCookieSettings = () => {
  if (typeof window === 'undefined' || !window.DelveCookieConsent) return;

  const consent = window.DelveCookieConsent;

  if (consent.showPreferences) {
    consent.showPreferences();
  } else if (consent.showSettings) {
    consent.showSettings();
  } else if (consent.openSettings) {
    consent.openSettings();
  } else if (consent.showBanner) {
    consent.showBanner();
  } else if (consent.show) {
    consent.show();
  }
};

const FOOTER_SECTIONS: FooterSection[] = [
  {
    title: 'Resources',
    links: [
      { label: 'All', href: '/resources' },
      { label: 'Articles', href: '/resources', resourceType: 'article' },
      { label: 'Playbooks', href: '/resources', resourceType: 'playbook' },
      { label: 'News', href: '/resources', resourceType: 'news' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms-of-service' },
      { label: 'Evaluation Terms of Service', href: '/evaluation-terms' },
      { label: 'Cookie Policy', href: '/cookie-policy' },
      {
        label: 'Cookie Settings',
        href: '#',
        action: handleOpenCookieSettings,
      },
      { label: 'Acceptable Use Policy', href: '/acceptable-use-policy' },
      { label: 'Data Processing Addendum', href: '/data-processing-addendum' },
      { label: 'Data Transfer Addendum', href: '/data-transfer-addendum' },
      { label: 'Security Addendum', href: '/security-addendum' },
      {
        label: 'Subprocessors',
        href: 'https://trust.heyarchie.com/subprocessors',
        external: true,
      },
    ],
  },
  {
    title: 'Contact',
    links: [
      { label: 'Contact Sales', href: '/contact-sales' },
      { label: 'Partner With Us', href: '/partner' },
      { label: 'Stay In The Loop', href: '/stay-in-the-loop' },
    ],
  },
];

const LINKEDIN_URL = 'https://www.linkedin.com/company/heyarchie/';

export const Footer = () => {
  return (
    <footer
      className="bg-blue-1000 border-t-4 border-orange-800 min-h-[424px]"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="flex gap-[55px] items-start max-w-[1440px] mx-auto px-[var(--spacing-96)] py-[68px] max-xl:px-[var(--spacing-48)] max-xl:gap-[var(--spacing-32)] max-lg:flex-col max-lg:px-[var(--spacing-24)] max-lg:py-[var(--spacing-48)] max-lg:gap-[var(--spacing-48)]">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center shrink-0 no-underline"
          aria-label="Archie Home"
        >
          <SignatureA size={49} colorVariant="white" />
        </Link>

        {/* Footer Sections */}
        <div className="flex justify-between flex-1 gap-[var(--spacing-48)] max-xl:gap-[var(--spacing-32)] max-lg:w-full max-lg:flex-wrap max-lg:gap-[var(--spacing-32)]">
          {FOOTER_SECTIONS.map((section) => (
            <div
              key={section.title}
              className="flex flex-col gap-[var(--spacing-16)] min-w-[200px] max-xl:min-w-[160px] max-lg:min-w-[calc(50%-16px)] max-sm:min-w-full"
            >
              <Typography as="h3" variant="h4" className="text-orange-200">
                {section.title}
              </Typography>
              <ul className="flex flex-col gap-[var(--spacing-16)] list-none p-0 m-0">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {link.action ? (
                      <button
                        type="button"
                        onClick={link.action}
                        className="font-body text-tiny text-orange-200 no-underline transition-colors duration-200 hover:text-white focus:text-white bg-transparent border-none cursor-pointer p-0"
                      >
                        {link.label}
                      </button>
                    ) : link.external ? (
                      <a
                        href={link.href}
                        className="font-body text-tiny text-orange-200 no-underline transition-colors duration-200 hover:text-white focus:text-white"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.label}
                      </a>
                    ) : link.resourceType ? (
                      <Link
                        to="/resources"
                        search={{ type: link.resourceType }}
                        className="font-body text-tiny text-orange-200 no-underline transition-colors duration-200 hover:text-white focus:text-white"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <Link
                        to={link.href}
                        className="font-body text-tiny text-orange-200 no-underline transition-colors duration-200 hover:text-white focus:text-white"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}

                {/* LinkedIn icon in Contact section */}
                {section.title === 'Contact' && (
                  <li>
                    <a
                      href={LINKEDIN_URL}
                      className="inline-flex items-center justify-center text-orange-200 transition-colors duration-200 hover:text-white focus:text-white rounded-[4px] border border-orange-200 p-[6px]"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on LinkedIn"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.68555 12H0.196289V3.98438H2.68555V12ZM9.01465 3.79004C11.5311 3.79004 11.999 5.44846 11.999 7.60742V12H9.51465V8.10352C9.51465 7.17224 9.5004 5.97656 8.22363 5.97656C6.92862 5.97671 6.73438 6.99179 6.73438 8.03418V12H4.25V3.98438H6.62793V5.08301H6.66016C6.9932 4.45295 7.80282 3.79015 9.01465 3.79004ZM1.44336 0C2.23892 3.22649e-05 2.88658 0.648534 2.88672 1.44531C2.88672 2.2422 2.239 2.89157 1.44336 2.8916C0.643068 2.8916 0 2.24222 0 1.44531C0.000132987 0.648514 0.643151 0 1.44336 0Z" fill="#FFF2E2"/>
                      </svg>
                    </a>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
};
